import { useQuery } from '@apollo/client';
import type { DescriptionPage_DifficultyLevel as DifficultyLevel } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';

import type { TopReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import type { ReviewsHeaderRatings } from 'bundles/reviews/components/ReviewsHeader';
import reviewsPageQuery from 'bundles/reviews/queries/ReviewsPage.graphql';
import ReviewsVariantCCourseHeaderDataQuery from 'bundles/reviews/queries/ReviewsVariantCCourseHeaderData.graphql';
import ReviewsVariantCProductCardDataQuery from 'bundles/reviews/queries/ReviewsVariantCProductCardData.graphql';
import type {
  ReviewsPageCdpMetadataFragment,
  ReviewsPageCourseFragment,
  ReviewsPageQuery,
  ReviewsPageQueryVariables,
  ReviewsPageSchemaReviewFragment,
} from 'bundles/reviews/queries/__generated__/ReviewsPage';
import type {
  ReviewsVariantCCourseHeaderDataQuery as ReviewsVariantCCourseHeaderDataQueryResult,
  ReviewsVariantCCourseHeaderDataQueryVariables,
} from 'bundles/reviews/queries/__generated__/ReviewsVariantCCourseHeaderData';
import type {
  ReviewsVariantCProductCardDataQuery as ReviewsVariantCProductCardDataQueryResult,
  ReviewsVariantCProductCardDataQueryVariables,
} from 'bundles/reviews/queries/__generated__/ReviewsVariantCProductCardData';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

type ReviewsPageCourseConnection = ReviewsPageQuery['NaptimeQueries']['XdpV1Resource']['course'];
type ReviewsPageCourseElement = NonNullable<NonNullable<ReviewsPageCourseConnection>['elements'][number]>;

export type ReviewsPageCourseInfo = ReviewsPageCourseFragment & {
  xdpMetadata: ReviewsPageCdpMetadataFragment;
};

export type ReviewsPageCdpMetadata = ReviewsPageCdpMetadataFragment['cdpMetadata'];
export type ReviewsPageSchemaReview = ReviewsPageSchemaReviewFragment;

type ReviewsVariantCHeaderData = {
  averageFiveStarRating?: number | null;
  courseName: string;
  description?: string | null;
  difficultyLevel?: DifficultyLevel | null;
  imageUrl?: string | null;
  marketingProductType?: string | null;
  partnerLogoUrl?: string | null;
  partnerName?: string | null;
  ratingCount?: number | null;
  ratingCountsByStar?: {
    fiveStarRatingCount?: number | null;
    fourStarRatingCount?: number | null;
    threeStarRatingCount?: number | null;
    twoStarRatingCount?: number | null;
    oneStarRatingCount?: number | null;
  } | null;
  skillTags: string[];
  totalDuration?: string | null;
};

type ReviewsPageDataResult = {
  loading: boolean;
  error?: Error;
  notFound: boolean;
  courseInfo: ReviewsPageCourseInfo | null;
  description: string | null;
  ratings: ReviewsHeaderRatings | null;
  topReviews: TopReview[];
  schemaReviews: ReviewsPageSchemaReview[] | null;
  variantCHeaderData: ReviewsVariantCHeaderData | null;
};

type Options = {
  includeVariantCHeaderData?: boolean;
};

const isDefined = <T>(value: T | null | undefined): value is T => value != null;

const isReviewsPageCourseInfo = (course: ReviewsPageCourseElement): course is ReviewsPageCourseInfo =>
  course.xdpMetadata.__typename === 'XdpV1_cdpMetadataMember';

export function useReviewsPageData({ includeVariantCHeaderData = false }: Options = {}): ReviewsPageDataResult {
  const params = useParams();
  const slug = params?.slug;

  const { data, loading, error } = useQuery<ReviewsPageQuery, ReviewsPageQueryVariables>(reviewsPageQuery, {
    variables: { slug: slug ?? '' },
    skip: !slug,
    context: {
      ssrBlocking: true,
    },
  });

  const {
    data: variantCCourseData,
    loading: variantCCourseLoading,
    error: variantCCourseError,
  } = useQuery<ReviewsVariantCCourseHeaderDataQueryResult, ReviewsVariantCCourseHeaderDataQueryVariables>(
    ReviewsVariantCCourseHeaderDataQuery,
    {
      variables: { slug: slug ?? '' },
      skip: !slug || !includeVariantCHeaderData,
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const elements = (data?.NaptimeQueries?.XdpV1Resource?.course?.elements ?? []).filter(isDefined);
  const courseInfo = elements.find(isReviewsPageCourseInfo) ?? null;
  const cdpMetadata = courseInfo?.xdpMetadata.cdpMetadata ?? null;
  const description = cdpMetadata?.description ?? null;
  const ratings =
    cdpMetadata?.ratings?.averageFiveStarRating != null &&
    cdpMetadata.ratings.ratingCount != null &&
    cdpMetadata.ratings.commentCount != null
      ? {
          averageFiveStarRating: cdpMetadata.ratings.averageFiveStarRating,
          ratingCount: cdpMetadata.ratings.ratingCount,
          commentCount: cdpMetadata.ratings.commentCount,
        }
      : null;
  const topReviews =
    cdpMetadata?.topReviews?.filter(
      (review): review is TopReview =>
        review?.averageFiveStarRating != null &&
        review.timestamp != null &&
        review.authorName != null &&
        review.comment != null
    ) ?? [];
  const schemaReviews = courseInfo?.fetchProductReviews?.elements?.filter(isDefined) ?? null;
  const courseId = courseInfo?.id?.split('~')[1] ?? '';

  const {
    data: variantCProductCardData,
    loading: variantCProductCardLoading,
    error: variantCProductCardError,
  } = useQuery<ReviewsVariantCProductCardDataQueryResult, ReviewsVariantCProductCardDataQueryVariables>(
    ReviewsVariantCProductCardDataQuery,
    {
      variables: {
        productCardKeys: courseId ? [{ id: courseId, canonicalType: 'COURSE' as const }] : [],
      },
      skip: !courseId || !includeVariantCHeaderData,
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const notFound = !loading && !!slug && elements.length === 0;

  if (notFound && inServerContext) {
    throw new NotFoundError();
  }

  const descriptionPage = variantCCourseData?.DescriptionPage?.queryCoursePage;
  const productCard = variantCProductCardData?.ProductCard?.queryByKeys?.[0];
  const productCanonical =
    productCard?.productTypeAttributes?.__typename === 'ProductCard_Course'
      ? productCard.productTypeAttributes.canonical
      : null;

  const variantCHeaderData = includeVariantCHeaderData
    ? {
        averageFiveStarRating:
          descriptionPage?.ratings?.averageFiveStarRating ?? cdpMetadata?.ratings?.averageFiveStarRating ?? null,
        courseName: productCanonical?.name ?? courseInfo?.name ?? '',
        description: descriptionPage?.description ?? cdpMetadata?.description ?? null,
        difficultyLevel: descriptionPage?.difficultyLevel ?? null,
        imageUrl: productCanonical?.promoPhotoUrl ?? null,
        marketingProductType: productCard?.marketingProductType ?? null,
        partnerLogoUrl:
          productCanonical?.partners?.[0]?.squareLogo ??
          descriptionPage?.partners?.[0]?.squareLogo ??
          cdpMetadata?.partners?.[0]?.squareLogo ??
          cdpMetadata?.partners?.[0]?.logo ??
          null,
        partnerName:
          productCanonical?.partners?.[0]?.name ??
          descriptionPage?.partners?.[0]?.name ??
          cdpMetadata?.partners?.[0]?.name ??
          null,
        ratingCount: descriptionPage?.ratings?.ratingCount ?? cdpMetadata?.ratings?.ratingCount ?? null,
        ratingCountsByStar: descriptionPage?.ratingCountsByStar ?? null,
        skillTags: (descriptionPage?.skillTags ?? [])
          .map((tag) => tag?.name?.trim())
          .filter((tag): tag is string => !!tag),
        totalDuration: descriptionPage?.material?.totalDuration ?? null,
      }
    : null;

  return {
    loading:
      !!slug &&
      (loading || (includeVariantCHeaderData && (variantCCourseLoading || (!!courseId && variantCProductCardLoading)))),
    error: error ?? variantCCourseError ?? variantCProductCardError ?? undefined,
    notFound,
    courseInfo,
    description,
    ratings,
    topReviews,
    schemaReviews,
    variantCHeaderData,
  };
}
